import { ArrowLeft, Info } from "lucide-react";
import { listReceipts } from "@/lib/api";
import { displayDate } from "@/lib/utils";
import { useData, matches, type ViewProps } from "@/lib/app-view";
import { PageHeader } from "@/components/page-header";
import { Empty, Loading, ErrorState } from "@/components/empty-state";
import { Table } from "@/components/data-table";
import { AppLink } from "@/components/app-link";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/panel";
import type { AppRole, GoodsReceipt } from "@/lib/types";
import { ControlPassportPanel } from "@/components/control-passport-panel";

export function Receipts({
  organisation,
  refresh,
  query,
  recordId,
  can,
  role,
  navigate,
}: Pick<ViewProps, "organisation" | "refresh" | "query" | "can"> & {
  recordId: string;
  role: AppRole;
  navigate: (view: string, recordId?: string) => void;
}) {
  const { data, error, loading } = useData(
    () => listReceipts(organisation.id),
    [organisation.id, refresh],
  );
  if (loading) return <Loading />;
  if (error || !data) return <ErrorState message={error} />;
  const rows = data.filter((row) =>
    matches(
      query,
      row.id,
      row.grn_no,
      row.purchase_orders?.po_no,
      row.purchase_orders?.vendors?.name,
      row.profiles?.full_name,
    ),
  );
  if (recordId) {
    const receipt = data.find((row) => row.id === recordId);
    if (!receipt)
      return (
        <PageHeader
          eyebrow="RECEIPT EVIDENCE"
          title="Goods receipt unavailable"
          description="This receipt is not available in the current organisation."
        >
          <Button variant="outline" asChild>
            <AppLink view="receipts" navigate={navigate}>
              <ArrowLeft className="size-4" aria-hidden="true" />
              All goods receipts
            </AppLink>
          </Button>
        </PageHeader>
      );
    return <ReceiptRecord row={receipt} organisationId={organisation.id} can={can} role={role} navigate={navigate} />;
  }
  return (
    <>
      <PageHeader
        eyebrow="RECEIVE"
        title="Goods receipts"
        description="Accepted and rejected quantities tied to each purchase order, with discrepancies visible."
      />
      <section
        className="boundary-panel mb-4"
        aria-labelledby="batch-boundary-title"
      >
        <Info className="size-5" aria-hidden="true" />
        <div>
          <h2 id="batch-boundary-title">Batch and expiry data boundary</h2>
          <p>
            The current receipt schema does not record batch, lot, serial,
            manufacture date or expiry date. RAQOZ therefore does not show empty
            batch fields or claim FIFO or FEFO stock rotation, allocation or
            enforcement.
          </p>
        </div>
      </section>
      {rows.length ? (
        <>
          <div className="hidden md:block">
            <Table
              heads={[
                "GRN",
                "Purchase order",
                "Vendor",
                "Received by",
                "Receipt evidence",
                "Timestamp",
                "Notes",
              ]}
            >
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <AppLink
                      view="receipts"
                      recordId={row.id}
                      navigate={navigate}
                      ariaLabel={`Open goods receipt ${row.grn_no}`}
                    >
                      <strong>{row.grn_no}</strong>
                    </AppLink>
                  </td>
                  <td>
                    <AppLink
                      view="orders"
                      recordId={row.purchase_order_id}
                      navigate={navigate}
                    >
                      {row.purchase_orders?.po_no}
                    </AppLink>
                  </td>
                  <td>{row.purchase_orders?.vendors?.name}</td>
                  <td>{row.profiles?.full_name}</td>
                  <td>
                    <ReceiptLines lines={row.goods_receipt_lines || []} />
                  </td>
                  <td>{displayDate(row.received_at, true)}</td>
                  <td>{row.notes || "—"}</td>
                </tr>
              ))}
            </Table>
          </div>
          <div className="grid gap-3 md:hidden" aria-label="Goods receipts">
            {rows.map((row) => (
              <article
                key={row.id}
                className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-sm)]"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-bold tracking-[.08em] text-[var(--text-muted)]">
                      GOODS RECEIPT
                    </p>
                    <h2 className="mt-1 text-base font-semibold">
                      <AppLink
                        view="receipts"
                        recordId={row.id}
                        navigate={navigate}
                        ariaLabel={`Open goods receipt ${row.grn_no}`}
                      >
                        {row.grn_no}
                      </AppLink>
                    </h2>
                  </div>
                  <span className="text-right text-xs text-[var(--text-muted)] tabular-nums">
                    {displayDate(row.received_at, true)}
                  </span>
                </div>
                <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <dt className="text-xs text-[var(--text-muted)]">Purchase order</dt>
                    <dd className="mt-0.5 font-medium">
                      <AppLink
                        view="orders"
                        recordId={row.purchase_order_id}
                        navigate={navigate}
                      >
                        {row.purchase_orders?.po_no}
                      </AppLink>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-[var(--text-muted)]">Received by</dt>
                    <dd className="mt-0.5">{row.profiles?.full_name || "User"}</dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-xs text-[var(--text-muted)]">Vendor</dt>
                    <dd className="mt-0.5">{row.purchase_orders?.vendors?.name}</dd>
                  </div>
                </dl>
                <div className="mt-4 border-t border-[var(--border)] pt-4">
                  <p className="mb-2 text-xs font-semibold text-[var(--text-secondary)]">
                    Accepted and rejected quantities
                  </p>
                  <ReceiptLines lines={row.goods_receipt_lines || []} />
                </div>
                {row.notes && (
                  <p className="mt-4 rounded-lg bg-[var(--surface-secondary)] p-3 text-sm text-[var(--text-secondary)]">
                    {row.notes}
                  </p>
                )}
              </article>
            ))}
          </div>
        </>
      ) : (
        <Empty
          title="No goods receipts yet"
          description="Receipts can be posted after a purchase order is issued."
        />
      )}
    </>
  );
}

function ReceiptRecord({
  row,
  organisationId,
  can,
  role,
  navigate,
}: {
  row: GoodsReceipt;
  organisationId: string;
  can: ViewProps["can"];
  role: AppRole;
  navigate: (view: string, recordId?: string) => void;
}) {
  return (
    <>
      <PageHeader
        eyebrow="RECEIPT EVIDENCE"
        title={row.grn_no}
        description="Recorded accepted and rejected quantities tied to the source purchase order."
      >
        <Button variant="outline" asChild>
          <AppLink view="receipts" navigate={navigate}>
            <ArrowLeft className="size-4" aria-hidden="true" />
            All goods receipts
          </AppLink>
        </Button>
      </PageHeader>
      <ControlPassportPanel
        organisationId={organisationId}
        checkpoint="receipt"
        requestNo={(row as any).purchase_orders?.rfqs?.requisitions?.request_no}
        viewerRole={role}
        capabilities={{
          manage: can("manage"),
          approve: can("approve"),
          procure: can("procure"),
          finance: can("finance"),
          receive: can("receive"),
        }}
        onNavigate={navigate}
      />

      <div className="grid gap-5 lg:grid-cols-[.8fr_1.2fr]">
        <Panel>
          <p className="eyebrow">RECEIPT FACTS</p>
          <h2 className="mt-1 font-serif text-xl">Recorded receipt</h2>
          <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-xs text-[var(--text-muted)]">Purchase order</dt>
              <dd className="mt-1 font-medium">
                <AppLink
                  view="orders"
                  recordId={row.purchase_order_id}
                  navigate={navigate}
                >
                  {row.purchase_orders?.po_no || "Open order"}
                </AppLink>
              </dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--text-muted)]">Vendor</dt>
              <dd className="mt-1 font-medium">{row.purchase_orders?.vendors?.name || "Not recorded"}</dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--text-muted)]">Received by</dt>
              <dd className="mt-1 font-medium">{row.profiles?.full_name || "Not recorded"}</dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--text-muted)]">Timestamp</dt>
              <dd className="mt-1 font-medium">{displayDate(row.received_at, true)}</dd>
            </div>
          </dl>
          <div className="mt-5 rounded-lg bg-[var(--surface-secondary)] p-3 text-sm text-[var(--text-secondary)]">
            <strong className="text-[var(--text-primary)]">Receipt note</strong>
            <p className="mt-1">{row.notes || "No receipt note was recorded."}</p>
          </div>
        </Panel>
        <Panel>
          <p className="eyebrow">LINE EVIDENCE</p>
          <h2 className="mt-1 font-serif text-xl">Accepted and rejected quantities</h2>
          <div className="mt-4 divide-y divide-[var(--border)] rounded-xl border border-[var(--border)]">
            {(row.goods_receipt_lines || []).map((line) => (
              <article className="grid gap-2 p-4 sm:grid-cols-[1fr_auto]" key={line.id}>
                <div>
                  <strong className="text-sm">{line.po_lines?.description || "PO line"}</strong>
                  {line.notes && (
                    <p className="mt-1 text-xs text-[var(--text-secondary)]">{line.notes}</p>
                  )}
                </div>
                <div className="text-sm tabular-nums sm:text-right">
                  <strong>{line.accepted_qty} accepted</strong>
                  <span className="mt-1 block text-xs text-[var(--text-secondary)]">
                    {line.rejected_qty} rejected
                  </span>
                </div>
              </article>
            ))}
          </div>
        </Panel>
      </div>
    </>
  );
}

function ReceiptLines({
  lines,
}: {
  lines: Array<{
    id: string;
    accepted_qty: number;
    rejected_qty: number;
    po_lines?: { description?: string } | null;
  }>;
}) {
  return (
    <div className="grid gap-1.5">
      {lines.map((line) => (
        <span className="block text-xs tabular-nums" key={line.id}>
          {line.po_lines?.description || "PO line"}: {line.accepted_qty} accepted
          {Number(line.rejected_qty) > 0
            ? ` · ${line.rejected_qty} rejected`
            : ""}
        </span>
      ))}
    </div>
  );
}
